import { Loader2, Pencil } from "lucide-react";
import {
  useCreate,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useState } from "react";

import { ReferenceField } from "@/components/admin/reference-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { useEsAdmin } from "../datos/useEsAdmin";
import { useResponsables } from "../datos/useResponsables";
import { claveCanal, claveResultadoPorCanal } from "../dominio/catalogos";
import { canalesEditables, puedeEditarNota } from "../dominio/notas";
import { claveEtapaOFase } from "../dominio/etapas";
import type { Canal, Nota } from "../dominio/tipos";
import { formatoFechaHora } from "./formato";

/**
 * Historial de notas de una ficha (SPEC.md §1), de todos sus proyectos, + añadir nota libre
 * (cuelga del proyecto en foco).
 */
export const NotasNegocio = ({
  negocioId,
  proyectoId,
}: {
  negocioId: Identifier;
  proyectoId?: Identifier | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [texto, setTexto] = useState("");
  const [editando, setEditando] = useState<Identifier | null>(null);
  const { data, isPending, refetch } = useGetList<Nota>("notas", {
    filter: { negocio_id: negocioId },
    sort: { field: "fecha", order: "DESC" },
    pagination: { page: 1, perPage: 200 },
  });
  const [create, { isPending: guardando }] = useCreate();
  const { identity } = useGetIdentity();
  const esAdmin = useEsAdmin();

  const guardar = () => {
    const t = texto.trim();
    if (!t) return;
    create(
      "notas",
      {
        data: {
          negocio_id: negocioId,
          proyecto_id: proyectoId ?? null,
          canal: "otro",
          texto: t,
          fecha: new Date().toISOString(),
        },
      },
      {
        onSuccess: () => {
          setTexto("");
          notify("nevora.notas.guardada", { type: "success" });
          refetch();
        },
        onError: (e) =>
          notify(
            e instanceof Error ? e.message : "ra.notification.http_error",
            {
              type: "error",
            },
          ),
      },
    );
  };

  return (
    <section className="space-y-4">
      <h2 className="etiqueta">{translate("nevora.negocios.historial")}</h2>
      <div className="space-y-2">
        <Textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={translate("nevora.notas.texto")}
          rows={2}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            disabled={!texto.trim() || guardando}
            onClick={guardar}
          >
            {guardando ? <Loader2 className="size-4 animate-spin" /> : null}
            {translate("nevora.notas.guardar")}
          </Button>
        </div>
      </div>
      {isPending ? (
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      ) : !data?.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("nevora.notas.vacio")}
        </p>
      ) : (
        <ol className="space-y-3">
          {data.map((nota) => (
            <li key={nota.id} className="rounded-lg border bg-card px-4 py-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{formatoFechaHora(nota.fecha)}</span>
                <Badge variant="outline">
                  {translate(claveCanal(nota.canal))}
                </Badge>
                {nota.resultado ? (
                  <Badge variant="secondary">
                    {translate(
                      claveResultadoPorCanal(nota.canal, nota.resultado),
                    )}
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    {translate(
                      nota.fase_nueva && nota.fase_nueva !== nota.fase_anterior
                        ? "nevora.notas.cambio_etapa"
                        : "nevora.notas.nota_libre",
                    )}
                  </Badge>
                )}
                {nota.fase_nueva && nota.fase_nueva !== nota.fase_anterior ? (
                  <span>
                    {translate("nevora.notas.paso_a", {
                      etapa: translate(claveEtapaOFase(nota.fase_nueva)),
                    })}
                  </span>
                ) : null}
                {nota.autor ? (
                  <span className="ml-auto">
                    <ReferenceField
                      record={nota}
                      source="autor"
                      reference="sales"
                      link={false}
                    />
                  </span>
                ) : null}
                {puedeEditarNota(nota, identity?.id, esAdmin) &&
                editando !== nota.id ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className={nota.autor ? "h-7 px-2" : "ml-auto h-7 px-2"}
                    onClick={() => setEditando(nota.id)}
                    aria-label={translate("nevora.notas.editar")}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                ) : null}
              </div>
              {editando === nota.id ? (
                <EditarNota
                  nota={nota}
                  onTerminar={(cambiada) => {
                    setEditando(null);
                    if (cambiada) refetch();
                  }}
                />
              ) : (
                <>
                  {nota.texto?.trim() ||
                  !(
                    nota.fase_nueva && nota.fase_nueva !== nota.fase_anterior
                  ) ? (
                    <p className="mt-1 whitespace-pre-wrap">
                      {nota.texto?.trim() || (
                        <span className="text-muted-foreground">
                          {translate("nevora.notas.sin_texto")}
                        </span>
                      )}
                    </p>
                  ) : null}
                  <Editada nota={nota} />
                </>
              )}
              {nota.reaccion || nota.objecion ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {nota.reaccion
                    ? `${translate("resources.notas.fields.reaccion")}: ${nota.reaccion}. `
                    : ""}
                  {nota.objecion
                    ? `${translate("resources.notas.fields.objecion")}: ${nota.objecion}.`
                    : ""}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

/** «Editada por X · fecha» (Robert, 09/10/2026). */
const Editada = ({ nota }: { nota: Nota }) => {
  const translate = useTranslate();
  const { nombre } = useResponsables();
  if (!nota.editada_at) return null;
  const quien = nombre(nota.editada_por);
  return (
    <p
      className="mt-1 text-xs text-muted-foreground"
      data-testid="nota-editada"
    >
      {quien
        ? translate("nevora.notas.editada", {
            quien,
            fecha: formatoFechaHora(nota.editada_at),
          })
        : translate("nevora.notas.editada_sin_quien", {
            fecha: formatoFechaHora(nota.editada_at),
          })}
    </p>
  );
};

/**
 * Corregir una nota (Robert, 09/10/2026): solo el texto y el canal, sin cambiar el tipo de
 * contacto. La base (trigger notas_editar) es quien lo garantiza y apunta quién y cuándo.
 */
const EditarNota = ({
  nota,
  onTerminar,
}: {
  nota: Nota;
  onTerminar: (cambiada: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [texto, setTexto] = useState(nota.texto ?? "");
  const [canal, setCanal] = useState<Canal>(nota.canal);
  const [update, { isPending }] = useUpdate();
  const canales = canalesEditables(nota.canal);

  const guardar = () =>
    update(
      "notas",
      { id: nota.id, data: { texto, canal }, previousData: nota },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("nevora.notas.corregida", { type: "success" });
          onTerminar(true);
        },
        onError: (e) =>
          notify(
            e instanceof Error ? e.message : "ra.notification.http_error",
            { type: "error" },
          ),
      },
    );

  return (
    <div className="mt-2 space-y-2" data-testid="editar-nota">
      <Textarea
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        rows={3}
        aria-label={translate("resources.notas.fields.texto")}
      />
      {canales.length > 1 ? (
        <div
          className="flex gap-2"
          role="group"
          aria-label={translate("resources.notas.fields.canal")}
        >
          {canales.map((c) => (
            <Button
              key={c}
              type="button"
              size="sm"
              variant={canal === c ? "default" : "outline"}
              aria-pressed={canal === c}
              onClick={() => setCanal(c)}
            >
              {translate(claveCanal(c))}
            </Button>
          ))}
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">
        {translate("nevora.notas.editar_ayuda")}
      </p>
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => onTerminar(false)}
        >
          {translate("nevora.notas.cancelar")}
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={
            isPending || (texto === (nota.texto ?? "") && canal === nota.canal)
          }
          onClick={guardar}
        >
          {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          {translate("nevora.notas.guardar_cambios")}
        </Button>
      </div>
    </div>
  );
};
